/**
 * Phase 3: Global Error Boundary with Recovery
 * Top-level error boundary with logging and recovery options
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw, Home, Bug } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `err_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // SECURITY: Only log sanitized error info, not full stack traces
    console.error('[GlobalErrorBoundary] Unhandled error:', {
      errorId: this.state.errorId,
      message: error.message?.replace(/[0-9a-f-]{36}/gi, '[UUID]'), // Redact UUIDs
      name: error.name,
    });
    
    this.setState({ errorInfo });
    
    // Non-blocking error logging
    this.logError(error, errorInfo);
  }

  private async logError(_error: Error, _errorInfo: ErrorInfo) {
    try {
      // SECURITY: In dev mode, show details but still redact sensitive data
      if (import.meta.env.DEV) {
        console.group('🚨 Error Details (Development Only)');
        console.error('Error Name:', _error.name);
        console.error('Error Message:', _error.message);
        // Note: Stack trace shown only in dev, but sensitive data still at risk
        // Consider implementing server-side error reporting for production
        console.groupEnd();
      }
    } catch {
      // Silently fail - don't compound errors
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorId } = this.state;
      const isDev = import.meta.env.DEV;

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-6 w-6" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertTitle>Application Error</AlertTitle>
                <AlertDescription>
                  An unexpected error occurred. Your work may not have been saved.
                </AlertDescription>
              </Alert>
              
              {errorId && (
                <p className="text-xs text-muted-foreground">
                  Error ID: <code className="bg-muted px-1 py-0.5 rounded">{errorId}</code>
                </p>
              )}
              
              {isDev && error && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground flex items-center gap-1">
                    <Bug className="h-4 w-4" />
                    Developer Details
                  </summary>
                  <pre className="mt-2 p-3 bg-muted rounded-md overflow-auto text-xs max-h-48">
                    {error.message}
                    {error.stack && `\n\n${error.stack}`}
                  </pre>
                </details>
              )}
            </CardContent>
            <CardFooter className="flex gap-2 flex-wrap">
              <Button onClick={this.handleRetry} variant="default">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button onClick={this.handleReload} variant="outline">
                Reload Page
              </Button>
              <Button onClick={this.handleGoHome} variant="ghost">
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
